import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { API_URL } from '../config/api';
import NotificationBell from './NotificationBell';
import {
  Search, ShoppingCart, Heart, User, ChevronDown, Menu, Phone, Mail, 
  X, LogOut, Shirt, ChevronRight, Zap, Home, ShoppingBasket, 
  Plane, Building2, Package, Sparkles, Command, ShieldCheck, Flame, Tag, HelpCircle
} from 'lucide-react';

const DEFAULT_CATEGORIES = [
  { id: 'Technology', name: 'Technology', description: 'Laptops, Smart Gadgets & Gear' },
  { id: 'Home Tech', name: 'Home Tech', description: 'Smart Refrigerators & Washing Tech' },
  { id: 'Lifestyle', name: 'Lifestyle', description: 'Inverters, Batteries & Furniture' },
  { id: 'Fashion', name: 'Fashion', description: 'Trending Apparel & Accessories' },
];

const getCategoryIcon = (name) => {
  const lower = (name || '').toLowerCase();
  if (lower.includes('tech') || lower.includes('electron')) return Zap;
  if (lower.includes('fashion') || lower.includes('apparel') || lower.includes('wear')) return Shirt;
  if (lower.includes('home') || lower.includes('appliance')) return Home;
  if (lower.includes('life') || lower.includes('style')) return Sparkles;
  if (lower.includes('essential') || lower.includes('grocery')) return ShoppingBasket;
  if (lower.includes('travel')) return Plane;
  if (lower.includes('estate') || lower.includes('real')) return Building2;
  return Tag;
};

const Navbar = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { getCartCount, getCartTotal } = useCart();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showCatMenu, setShowCatMenu] = useState(false);
  const [isFixed, setIsFixed] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  const [dbCategories, setDbCategories] = useState(DEFAULT_CATEGORIES);
  
  const catMenuRef = useRef(null);
  const searchInputRef = useRef(null);

  // Fetch dynamic categories safely from API with fallback
  useEffect(() => {
    let isMounted = true;
    const fetchCategories = async () => {
      try {
        const res = await fetch(`${API_URL}/api/categories`);
        const contentType = res.headers.get('content-type') || '';
        if (res.ok && contentType.includes('application/json')) {
          const json = await res.json();
          if (json.success && Array.isArray(json.data) && json.data.length > 0) {
            if (isMounted) setDbCategories(json.data);
            return;
          }
        }

        // Secondary fallback to filters options endpoint
        const filterRes = await fetch(`${API_URL}/api/products/filters/options`);
        const filterType = filterRes.headers.get('content-type') || '';
        if (filterRes.ok && filterType.includes('application/json')) {
          const filterJson = await filterRes.json();
          if (filterJson.success && filterJson.filters?.categories && filterJson.filters.categories.length > 0) {
            const parsed = filterJson.filters.categories.map(c => ({
              id: c,
              name: c,
              description: `Explore premium ${c} products`
            }));
            if (isMounted) setDbCategories(parsed);
            return;
          }
        }
      } catch (err) {
        // Quietly maintain DEFAULT_CATEGORIES fallback
      }
    };
    fetchCategories();
    return () => { isMounted = false; };
  }, []);

  // Keyboard shortcut listener (Cmd+K / Ctrl+K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Sticky navbar listener & click outside dropdown handler
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 30) setIsFixed(true);
      else setIsFixed(false);
    };

    const handleClickOutside = (event) => {
      if (catMenuRef.current && !catMenuRef.current.contains(event.target)) {
        setShowCatMenu(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Reset dropdowns on route navigation
  useEffect(() => {
    setShowMobileMenu(false);
    setShowCatMenu(false);
    setShowAccountDropdown(false);
  }, [location.pathname]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    if (location.pathname !== '/') {
      navigate(`/?q=${encodeURIComponent(searchQuery)}`);
    } else {
      setSearchParams({ q: searchQuery });
    }
  };

  const activeCategories = dbCategories.length > 0 ? dbCategories : DEFAULT_CATEGORIES;

  return (
    <header className="w-full font-sans relative z-50 select-none">
      
      {/* ── 1. TOP TRUST STRIP WITH MARQUEE ANIMATION ── */}
      <div className="bg-gradient-to-r from-orange-600 via-amber-500 to-orange-500 text-white py-1.5 text-[11px] font-extrabold uppercase tracking-wider border-b border-orange-400/40 relative overflow-hidden shadow-xs">
        
        {/* Soft Ambient Light Gradient Bar */}
        <div className="absolute inset-0 bg-gradient-to-r from-white/10 via-amber-300/20 to-white/10 pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between gap-4 relative z-10">
          
          {/* Desktop Left Trust Bulletins */}
          <div className="hidden lg:flex items-center gap-6 text-orange-50">
            <span className="flex items-center gap-1.5 text-white font-black">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
              100% Trusted Products
            </span>
            <span className="hover:text-white transition-colors">Fast &amp; Safe Delivery</span>
            <span className="hover:text-white transition-colors">Secure Payments</span>
            <span className="hover:text-white transition-colors">Easy Monthly Plans</span>
          </div>

          {/* Mobile & Tablet Marquee Ticker */}
          <div className="lg:hidden flex-1 overflow-hidden relative">
            <div className="flex animate-marquee whitespace-nowrap gap-8 text-orange-50 text-[10px]">
              <span className="flex items-center gap-1.5 text-white font-black">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                100% TRUSTED PRODUCTS
              </span>
              <span>• FAST &amp; SAFE DELIVERY</span>
              <span>• SECURE PAYMENTS</span>
              <span>• EASY MONTHLY PLANS</span>
              <span className="flex items-center gap-1.5 text-white font-black">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                100% TRUSTED PRODUCTS
              </span>
              <span>• FAST &amp; SAFE DELIVERY</span>
            </div>
          </div>

          {/* Right Action Links */}
          <div className="flex items-center gap-4 text-orange-50 shrink-0">
            <Link to="/track" className="hover:text-white flex items-center gap-1 transition-colors text-[10px] md:text-[11px]">
              <Package className="w-3.5 h-3.5 text-white" />
              <span>Track Order</span>
            </Link>
            <Link to="/contact" className="hover:text-white flex items-center gap-1 transition-colors text-[10px] md:text-[11px]">
              <HelpCircle className="w-3.5 h-3.5 text-white" />
              <span>Help &amp; Support</span>
            </Link>
          </div>

        </div>
      </div>

      {/* ── 2. MAIN FLOATING LIGHT GLASS NAVBAR ── */}
      <div className={`w-full transition-all duration-500 ${
        isFixed 
          ? 'fixed top-0 left-0 right-0 glass-panel border-b border-slate-200/80 shadow-lg backdrop-blur-2xl py-2 bg-white/95 z-50 animate-fade-in-down' 
          : 'bg-white border-b border-slate-200 py-3'
      }`}>
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between gap-3 lg:gap-8">

          {/* Left: Brand Identity */}
          <Link to="/" className="flex items-center gap-2.5 sm:gap-3 group shrink-0">
            <div className="relative">
              <div className="absolute -inset-1 bg-gradient-to-r from-orange-500 via-amber-500 to-blue-500 rounded-2xl blur opacity-40 group-hover:opacity-100 transition duration-500 animate-pulse"></div>
              <img 
                src="/images/logoicon.png" 
                alt="BrightBazaar Logo" 
                className="relative w-9 h-9 sm:w-11 sm:h-11 rounded-xl object-contain bg-white p-1 border border-orange-200 shadow-md group-hover:scale-105 transition-transform"
              />
            </div>
            <div className="flex flex-col">
              <span className="text-lg sm:text-2xl font-black tracking-tight text-slate-900 uppercase italic">
                BRIGHT<span className="text-orange-600 font-black">BAZAAR</span>
              </span>
              <span className="text-[8px] sm:text-[9px] font-extrabold tracking-widest text-slate-500 uppercase -mt-1 hidden sm:block">
                PREMIUM ECOMMERCE
              </span>
            </div>
          </Link>

          {/* Center: Command Search Bar (Desktop & Tablet) */}
          <form 
            onSubmit={handleSearchSubmit}
            className="hidden md:flex flex-1 max-w-2xl items-center bg-slate-100/90 border border-slate-200/90 rounded-2xl px-3 py-1.5 focus-within:border-orange-500 focus-within:ring-2 focus-within:ring-orange-500/20 transition-all shadow-inner relative group"
          >
            {/* Category Dropdown Trigger */}
            <div className="relative" ref={catMenuRef}>
              <button
                type="button"
                onClick={() => setShowCatMenu(!showCatMenu)}
                className="flex items-center gap-1.5 text-xs font-bold text-slate-700 hover:text-slate-900 px-3 py-1.5 rounded-xl bg-white hover:bg-slate-200/60 transition-colors border border-slate-200/60 shadow-sm"
              >
                <span>Categories</span>
                <ChevronDown className={`w-3.5 h-3.5 text-orange-600 transition-transform duration-300 ${showCatMenu ? 'rotate-180' : ''}`} />
              </button>

              {/* Dynamic Database Dropdown Menu */}
              {showCatMenu && (
                <div className="absolute top-12 left-0 w-64 bg-white border border-slate-200 rounded-2xl p-2 shadow-2xl z-50 animate-fade-in-down max-h-80 overflow-y-auto">
                  <Link
                    to="/"
                    onClick={() => setShowCatMenu(false)}
                    className="flex items-center justify-between p-2.5 rounded-xl hover:bg-orange-50 text-slate-700 hover:text-slate-900 transition-all group border-b border-slate-100 mb-1"
                  >
                    <div className="flex items-center gap-2.5">
                      <ChevronRight className="w-4 h-4 text-orange-600 group-hover:scale-110 transition-transform" />
                      <span className="text-xs font-black uppercase text-slate-900">All Categories</span>
                    </div>
                    <span className="text-[9px] font-black tracking-widest px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 border border-orange-200">
                      ALL
                    </span>
                  </Link>

                  {activeCategories.map((cat) => {
                    const Icon = getCategoryIcon(cat.name);
                    return (
                      <Link
                        key={cat.id || cat.name}
                        to={`/categories/${encodeURIComponent(cat.name)}/products`}
                        onClick={() => setShowCatMenu(false)}
                        className="flex items-center justify-between p-2.5 rounded-xl hover:bg-orange-50 text-slate-700 hover:text-slate-900 transition-all group"
                      >
                        <div className="flex items-center gap-2.5">
                          <Icon className="w-4 h-4 text-orange-600 group-hover:scale-110 transition-transform" />
                          <span className="text-xs font-bold">{cat.name}</span>
                        </div>
                        <span className="text-[9px] font-black tracking-widest px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200 uppercase">
                          LIVE
                        </span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="h-5 w-px bg-slate-300 mx-2" />

            {/* Input field */}
            <Search className="w-4 h-4 text-slate-400 shrink-0 ml-1 group-focus-within:text-orange-600 transition-colors" />
            <input 
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search 1,000+ products, tech, fashion..."
              className="w-full bg-transparent px-3 py-1 text-xs text-slate-900 placeholder-slate-400 outline-none font-medium"
            />

            {/* Shortcut Badge */}
            <div className="hidden lg:flex items-center gap-1 text-[10px] font-black text-slate-400 bg-white border border-slate-200 px-2 py-1 rounded-lg shrink-0 shadow-sm">
              <Command className="w-3 h-3" />
              <span>K</span>
            </div>

            <button
              type="submit"
              className="ml-2 skeuo-btn text-white text-xs font-black px-4 py-1.5 rounded-xl uppercase tracking-wider shrink-0 flex items-center gap-1.5 transition-transform hover:scale-105 active:scale-95"
            >
              <span>Search</span>
            </button>
          </form>

          {/* Right Action Hub */}
          <div className="flex items-center gap-2 sm:gap-3.5">

            {/* Wishlist */}
            <Link 
              to="/wishlist" 
              className="hidden sm:flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-2xl bg-slate-100 hover:bg-red-50 border border-slate-200 hover:border-red-200 text-slate-600 hover:text-red-500 transition-all relative group shadow-sm"
              title="Wishlist"
            >
              <Heart className="w-4 h-4 group-hover:scale-110 transition-transform" />
            </Link>

            {/* Notifications */}
            <div className="hidden sm:block">
              <NotificationBell />
            </div>

            {/* Claymorphic Cart Button */}
            <Link 
              to="/cart" 
              className="flex items-center gap-2 sm:gap-2.5 px-3 sm:px-3.5 py-1.5 sm:py-2 rounded-2xl clay-card border border-slate-200 text-slate-900 hover:border-orange-500/50 transition-all group relative shadow-sm"
            >
              <div className="relative">
                <ShoppingCart className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600 group-hover:scale-110 transition-transform" />
                {getCartCount() > 0 && (
                  <span className="absolute -top-2.5 -right-2.5 min-w-[20px] h-[20px] px-1 bg-gradient-to-r from-orange-500 to-amber-500 text-white text-[10px] font-black rounded-full flex items-center justify-center border-2 border-white shadow-md animate-bounce">
                    {getCartCount()}
                  </span>
                )}
              </div>
              <div className="hidden lg:flex flex-col text-left">
                <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest -mb-0.5">My Cart</span>
                <span className="text-xs font-black text-orange-600">₹{getCartTotal().toLocaleString()}</span>
              </div>
            </Link>

            {/* User Account */}
            {user ? (
              <div className="relative">
                <button
                  onClick={() => setShowAccountDropdown(!showAccountDropdown)}
                  className="flex items-center gap-1.5 p-1 sm:p-1.5 rounded-2xl bg-slate-100 hover:bg-slate-200 border border-slate-200 text-slate-900 transition-all shadow-sm"
                >
                  <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-xl bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center font-black text-white text-xs shadow-md">
                    {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                  </div>
                  <ChevronDown className="w-3.5 h-3.5 text-slate-500 hidden sm:block" />
                </button>

                {showAccountDropdown && (
                  <div className="absolute right-0 top-12 w-56 bg-white border border-slate-200 rounded-2xl p-2 shadow-2xl z-50 animate-fade-in-down">
                    <div className="px-3 py-2 border-b border-slate-100 mb-1">
                      <p className="text-xs font-black text-slate-900 truncate">{user.name}</p>
                      <p className="text-[10px] text-slate-500 truncate">{user.email}</p>
                    </div>
                    <Link to="/account" className="flex items-center gap-2.5 p-2 rounded-xl text-xs font-bold text-slate-700 hover:text-slate-900 hover:bg-slate-100 transition-colors">
                      <User className="w-4 h-4 text-orange-600" /> Account Settings
                    </Link>
                    <Link to="/orders" className="flex items-center gap-2.5 p-2 rounded-xl text-xs font-bold text-slate-700 hover:text-slate-900 hover:bg-slate-100 transition-colors">
                      <Package className="w-4 h-4 text-blue-600" /> My Orders
                    </Link>
                    <Link to="/coupons" onClick={() => setShowAccountDropdown(false)} className="flex items-center gap-2.5 p-2 rounded-xl text-xs font-bold text-slate-700 hover:text-slate-900 hover:bg-slate-100 transition-colors">
                      <Tag className="w-4 h-4 text-purple-600" /> My Coupons &amp; Offers
                    </Link>
                    <button
                      onClick={logout}
                      className="w-full flex items-center gap-2.5 p-2 rounded-xl text-xs font-bold text-red-600 hover:bg-red-50 transition-colors mt-1"
                    >
                      <LogOut className="w-4 h-4" /> Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link 
                to="/login"
                className="skeuo-btn text-white text-xs font-black px-3.5 py-1.5 sm:px-4 sm:py-2 rounded-2xl uppercase tracking-wider flex items-center gap-1.5"
              >
                <User className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Login</span>
              </Link>
            )}

            {/* Mobile Menu Hamburger Trigger */}
            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="md:hidden w-9 h-9 rounded-2xl bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-900 hover:bg-slate-200 transition-colors shadow-sm"
              aria-label="Toggle Navigation Menu"
            >
              {showMobileMenu ? <X className="w-5 h-5 text-orange-600" /> : <Menu className="w-5 h-5 text-slate-800" />}
            </button>

          </div>
        </div>
      </div>

      {/* ── 3. BENTO CATEGORY STRIP ── */}
      <div className="bg-slate-100/90 border-b border-slate-200/80 py-2 overflow-x-auto no-scrollbar">
        <div className="max-w-7xl mx-auto px-4 flex items-center gap-3 min-w-max">
          <span className="text-[10px] font-black tracking-widest text-slate-500 uppercase flex items-center gap-1 pr-2 border-r border-slate-300 shrink-0">
            <Tag className="w-3 h-3 text-orange-600" /> BROWSE:
          </span>
          <Link
            to="/"
            className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-white hover:bg-orange-50 border border-slate-200 hover:border-orange-300 text-slate-700 hover:text-slate-900 transition-all text-xs font-bold group shadow-sm"
          >
            <ChevronRight className="w-3.5 h-3.5 text-orange-600 group-hover:scale-110 transition-transform" />
            <span>All Categories</span>
          </Link>
          {activeCategories.map((cat) => {
            const Icon = getCategoryIcon(cat.name);
            return (
              <Link
                key={cat.id || cat.name}
                to={`/categories/${encodeURIComponent(cat.name)}/products`}
                className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-white hover:bg-orange-50 border border-slate-200 hover:border-orange-300 text-slate-700 hover:text-slate-900 transition-all text-xs font-bold group shadow-sm"
              >
                <Icon className="w-3.5 h-3.5 text-orange-600 group-hover:scale-110 transition-transform" />
                <span>{cat.name}</span>
                <span className="text-[9px] font-extrabold px-1.5 py-0.2 bg-slate-100 rounded-full text-slate-500 group-hover:text-orange-700 uppercase">
                  LIVE
                </span>
              </Link>
            );
          })}
        </div>
      </div>

      {/* ── 4. SUPER RESPONSIVE MOBILE DRAWER ── */}
      {showMobileMenu && (
        <div className="md:hidden bg-white/95 backdrop-blur-xl border-b border-slate-200/90 p-4 space-y-4 animate-fade-in-down shadow-2xl z-50">
          
          {/* Mobile Command Search Bar */}
          <form onSubmit={handleSearchSubmit} className="flex gap-2">
            <div className="flex-1 flex items-center bg-slate-100 border border-slate-200 rounded-2xl px-3 py-2">
              <Search className="w-4 h-4 text-slate-400 mr-2 shrink-0" />
              <input 
                type="text" 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search products, brands..."
                className="w-full bg-transparent text-xs text-slate-900 outline-none font-medium"
              />
            </div>
            <button type="submit" className="skeuo-btn text-white px-4 text-xs font-bold rounded-2xl">
              Search
            </button>
          </form>

          {/* Quick Mobile Navigation Grid */}
          <div className="grid grid-cols-2 gap-2 pt-1">
            <Link to="/" className="p-3 rounded-2xl bg-slate-50 hover:bg-orange-50 border border-slate-200 text-xs font-black text-slate-900 flex items-center gap-2">
              <Home className="w-4 h-4 text-orange-600" /> Home
            </Link>
            <Link to="/coupons" className="p-3 rounded-2xl bg-slate-50 hover:bg-orange-50 border border-slate-200 text-xs font-black text-slate-900 flex items-center gap-2">
              <Tag className="w-4 h-4 text-purple-600" /> Coupons &amp; Offers
            </Link>
            <Link to="/orders" className="p-3 rounded-2xl bg-slate-50 hover:bg-orange-50 border border-slate-200 text-xs font-black text-slate-900 flex items-center gap-2">
              <Package className="w-4 h-4 text-blue-600" /> My Orders
            </Link>
            <Link to="/wishlist" className="p-3 rounded-2xl bg-slate-50 hover:bg-orange-50 border border-slate-200 text-xs font-black text-slate-900 flex items-center gap-2">
              <Heart className="w-4 h-4 text-red-500" /> Wishlist
            </Link>
          </div>

          {/* Mobile Category Quick Chips */}
          <div className="pt-2 border-t border-slate-100">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Live Categories</p>
            <div className="flex flex-wrap gap-1.5">
              {activeCategories.map(cat => (
                <Link
                  key={cat.id || cat.name}
                  to={`/categories/${encodeURIComponent(cat.name)}/products`}
                  className="px-3 py-1 rounded-xl bg-slate-100 hover:bg-orange-100 text-slate-700 hover:text-orange-700 text-xs font-bold border border-slate-200 transition-colors"
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>

        </div>
      )}

    </header>
  );
};

export default Navbar;
